import { Link } from '@tanstack/react-router';
import { cn } from 'cn';
import type * as React from 'react';

import AdnocLogo from '@/images/clients/adnoc.png?logo';
import BpLogo from '@/images/clients/bp.png?logo';
import ChevronLogo from '@/images/clients/chevron.png?logo';
import EquinorLogo from '@/images/clients/equinor.png?logo';
import ExxonMobilLogo from '@/images/clients/exxonmobil.png?logo';
import RightShipLogo from '@/images/clients/rightship.png?logo';
import ShellLogo from '@/images/clients/shell.png?logo';
import { blurBackground, photoSources } from '@/lib/images';
import { m } from '@/paraglide/messages';
import { clients } from '@/site-config';

export function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={cn('mb-3 font-semibold text-brand', className)}>{children}</p>;
}

interface PhotoProps {
  src: string;
  alt: string;
  className?: string;
  /** CSS `object-position`, to keep the vessel in frame when the photo is cropped. */
  position?: string;
  priority?: boolean;
  /** The `sizes` hint for picking a width; the default suits a photo beside text. */
  sizes?: string;
}

/** A rounded, cropped photo that paints its blurred placeholder while it loads. */
export function Photo({
  src,
  alt,
  className,
  position,
  priority,
  sizes = '(min-width: 1024px) 50vw, 100vw',
}: PhotoProps) {
  const sources = photoSources(src);
  return (
    <div className={cn('bg-mist relative overflow-hidden rounded-3xl', className)} style={blurBackground(src)}>
      <picture>
        {sources && <source type='image/avif' srcSet={sources.avif} sizes={sizes} />}
        <img
          src={src}
          srcSet={sources?.jpeg}
          sizes={sources && sizes}
          alt={alt}
          loading={priority ? undefined : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          className='absolute inset-0 size-full object-cover'
          style={position ? { objectPosition: position } : undefined}
        />
      </picture>
    </div>
  );
}

interface PageHeroProps {
  eyebrow: string;
  title: string;
  lead: React.ReactNode;
  image?: Omit<PhotoProps, 'className' | 'priority'>;
  children?: React.ReactNode;
}

/** The light intro every inner page opens with: text on the left, an optional photo on the right. */
export function PageHero({ eyebrow, title, lead, image, children }: PageHeroProps) {
  return (
    <section className='mx-auto flex w-full max-w-310 flex-wrap items-center gap-14 px-5 pt-12 pb-16 sm:px-8 sm:pt-16 sm:pb-18'>
      <div className='min-w-0 flex-[1_1_460px]'>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className='mb-5 text-[clamp(38px,4.4vw,56px)] leading-[1.08] font-bold tracking-[-0.02em]'>{title}</h1>
        <div className='text-body max-w-140 text-[19px] leading-[1.65]'>{lead}</div>
        {children}
      </div>
      {image && <Photo {...image} priority className='aspect-3/2 min-w-0 flex-[1_1_460px]' />}
    </section>
  );
}

/** The closing call to action on most pages; the contact details themselves live on /contact. */
export function ContactCta({
  title = m.contact_cta_title(),
  text = m.contact_cta_text(),
}: {
  title?: string;
  text?: string;
}) {
  return (
    <section className='border-line border-t bg-white'>
      <div className='mx-auto flex w-full max-w-310 flex-wrap items-center justify-between gap-x-10 gap-y-6 px-5 py-12 sm:px-8 sm:py-18'>
        <div className='min-w-0 flex-[1_1_420px]'>
          <h2 className='mb-2.5 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>{title}</h2>
          <p className='text-body text-lg/relaxed'>{text}</p>
        </div>
        <Link
          to='/contact'
          className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-brand px-7 font-semibold text-white transition-colors hover:bg-[#1f4f8a] disabled:opacity-70'
        >
          {m.contact_cta_button()}
        </Link>
      </div>
    </section>
  );
}

const logos: Record<(typeof clients)[number], string> = {
  Shell: ShellLogo,
  BP: BpLogo,
  Chevron: ChevronLogo,
  ExxonMobil: ExxonMobilLogo,
  ADNOC: AdnocLogo,
  Equinor: EquinorLogo,
  RightShip: RightShipLogo,
};

/** A strip of client logos. */
export function ClientLogos({ label }: { label: string }) {
  return (
    <section className='border-line border-y'>
      <div className='mx-auto grid w-full max-w-310 grid-cols-3 place-items-center gap-x-4 gap-y-6 px-5 py-9 sm:flex sm:flex-wrap sm:justify-between sm:gap-x-10 sm:px-8'>
        <span className='text-subtle col-span-full justify-self-start font-semibold'>{label}</span>
        {clients.map((name) => (
          <img
            key={name}
            src={logos[name]}
            alt={name}
            loading='lazy'
            className='h-11 w-auto max-w-32.5 object-contain'
          />
        ))}
      </div>
    </section>
  );
}
